import { useState, useRef } from 'react';
import venueDashboardApiService from '../../../services/venueDashboardApi';
import userProfileService from '../../../services/userProfileService';
import { clearVenueDashboardCache } from '../../../utils/venueDashboardCache';
import { clearVenueRecordCache } from '../../../utils/venueRecordCache';
import { clearAvailabilityCache } from '../../../utils/availabilityCache';
import { clearVenueDiscoveryCache } from '../../../utils/venueDiscoveryCache';
import {
  hostPhotoFullUrlFromUserInput,
  resolveStoredHostPhotoUrl
} from '../../../utils/hostPhotoUrl';
import {
  hostRulesToFormText,
  extractHostRulesFromEventRules,
  syncHostRulesWithAlcoholSettings
} from '../../../utils/bookingHostRules';

const invalidateVenueCaches = (venueId) => {
  if (!venueId) return;
  clearVenueDashboardCache(venueId);
  clearVenueRecordCache(venueId);
  clearAvailabilityCache(venueId);
  clearVenueDiscoveryCache(venueId);
};

const splitAccountName = (name) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
};

/**
 * Custom hook for managing settings-related handlers
 */
const VENUE_TYPE_MAP = {
  cafe: 'cafe', café: 'cafe', cafè: 'cafe',
  conference_room: 'conference_room', 'conference room': 'conference_room',
  banquet_hall: 'banquet_hall', 'banquet hall': 'banquet_hall',
  outdoor_space: 'outdoor_space', 'outdoor space': 'outdoor_space',
  studio: 'studio', loft: 'loft', hotel: 'hotel',
  restaurant: 'restaurant', auditorium: 'auditorium'
};
const normalizeVenueType = (v) => {
  if (!v) return '';
  const key = String(v).toLowerCase().trim();
  return VENUE_TYPE_MAP[key] || key.replace(/\s+/g, '_') || '';
};

const splitMultiline = (value) => {
  const seen = new Set();
  return String(value || '')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => {
      if (!line) return false;
      const key = line.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const toAvailableItems = (lines = []) => lines.map((name) => ({ name, available: true }));

const toLines = (items = [], mapper = (item) => item) =>
  (Array.isArray(items) ? items : [])
    .map((item) => mapper(item))
    .map((value) => (value == null ? '' : String(value).trim()))
    .filter(Boolean)
    .join('\n');

const normalizeApiVenueToForm = (venueData, venueId) => {
  if (!venueData) return null;
  const amenities = venueData.amenities || {};
  const foodBeverage = amenities.food_beverage || amenities.foodBeverage || {};
  const eventRules = amenities.event_rules || amenities.eventRules || {};
  const alcohol = foodBeverage.alcohol || {};
  const inHouseCatering = Array.isArray(foodBeverage.inHouseCatering)
    ? foodBeverage.inHouseCatering
    : [];
  const externalCatering = Array.isArray(foodBeverage.externalCatering)
    ? foodBeverage.externalCatering
    : [];
  const allowedEvents = Array.isArray(eventRules.allowedEvents)
    ? eventRules.allowedEvents
    : [];
  const hostRules = extractHostRulesFromEventRules(eventRules);
  const amenityItems = Array.isArray(amenities.amenities)
    ? amenities.amenities
    : (Array.isArray(venueData.facilities) ? venueData.facilities : []);
  const primaryImage = venueData.primary_image_url ||
    (venueData.images?.[0]?.image_url) ||
    venueData.image_url || venueData.image || '';
  const hostRaw =
    venueData.host_photo_url ||
    venueData.owner_photo_url ||
    venueData.contact_photo_url ||
    '';
  const hostResolved = resolveStoredHostPhotoUrl(hostRaw, {
    venueId,
    images: venueData.images
  });
  return {
    name: venueData.name || '',
    address: venueData.address || '',
    description: venueData.description || '',
    host_photo_url: hostResolved ? String(hostResolved).trim() : '',
    max_capacity: venueData.max_capacity || venueData.seating_capacity || 0,
    seating_capacity: venueData.seating_capacity ?? venueData.max_capacity ?? 0,
    standing_capacity: venueData.standing_capacity ?? 0,
    venue_type: normalizeVenueType(venueData.venue_type || venueData.type || ''),
    map_link: venueData.map_link || venueData.mapLink || venueData.google_maps_url || '',
    image_url: primaryImage,
    serves_alcohol: venueData.serves_alcohol ?? alcohol.venueProvides ?? false,
    outside_alcohol_allowed: venueData.outside_alcohol_allowed ?? alcohol.byoAllowed ?? false,
    corkage_fee_for_byo: alcohol.corkageFee ?? false,
    in_house_catering: venueData.in_house_catering_allowed ?? (Array.isArray(foodBeverage.inHouseCatering) && foodBeverage.inHouseCatering.length > 0),
    external_catering_allowed: venueData.external_catering_allowed ?? (Array.isArray(foodBeverage.externalCatering) && foodBeverage.externalCatering.length > 0),
    amenities: venueData.amenities,
    facilities: venueData.facilities,
    in_house_catering_options_text: toLines(
      inHouseCatering,
      (item) => (typeof item === 'string' ? item : (item?.name || item?.label || ''))
    ),
    external_catering_options_text: toLines(
      externalCatering,
      (item) => (typeof item === 'string' ? item : (item?.name || item?.label || ''))
    ),
    allowed_events_text: toLines(
      allowedEvents,
      (item) => (typeof item === 'string' ? item : (item?.name || item?.label || ''))
    ),
    host_rules_text: hostRulesToFormText(hostRules),
    amenities_text: toLines(
      amenityItems,
      (item) => (typeof item === 'string' ? item : (item?.name || item?.label || ''))
    ),
    cancellation_policy_text:
      eventRules.cancellationPolicy ||
      eventRules.cancellation_policy ||
      venueData.cancellation_policy ||
      '',
    payment_link: venueData.payment_link || venueData.payment_url || ''
  };
};

export const useSettingsHandlers = (venueId, {
  operatingHours,
  setOperatingHours,
  holidays,
  setHolidays,
  dynamicPricing,
  setDynamicPricing,
  defaultPrice,
  setDefaultPrice,
  loadedDefaultPrice,
  defaultPerPersonPrice,
  setDefaultPerPersonPrice,
  loadedDefaultPerPersonPrice,
  dynamicPricingPerPerson,
  setDynamicPricingPerPerson,
  accountSettings,
  setAccountSettings,
  setOwnerAccountSettings,
  flatPricing,
  setFlatPricing,
  bookingRules,
  setBookingRules,
  operatingHoursPerPerson,
  setOperatingHoursPerPerson,
  venueDetails,
  setVenueDetails,
  primarySpaceId,
  settingsSpaceId = null,
  multiSpace = false,
  onNotify,
  isOwnerViewingOwnVenue = false,
}) => {
  /** null = venue-wide default (applies as fallback for all spaces at this venue). */
  const resolveHoursSpaceScope = () => settingsSpaceId || null;

  const requirePricingSpaceScope = () => {
    if (multiSpace && !settingsSpaceId) {
      throw new Error('Select a space before saving pricing or catalog changes.');
    }
    return settingsSpaceId || null;
  };

  const notify = (type, title, message = '') => {
    if (onNotify) onNotify({ type, title, message });
    else alert(message ? `${title}\n${message}` : title);
  };
  // Holiday input state - support multiple draft rows
  const [draftHolidays, setDraftHolidays] = useState([{ name: '', date: '' }]);
  
  // Dynamic pricing draft rows (like holidays) - Save posts these, Add another adds empty row
  const [draftDynamicPricing, setDraftDynamicPricing] = useState([{ date: '', endDate: '', startTime: '', endTime: '', price: '' }]);
  const [draftDynamicPricingPerPerson, setDraftDynamicPricingPerPerson] = useState([{ date: '', endDate: '', startTime: '', endTime: '', price: '' }]);

  // Flat pricing: draft rows (Add adds here), pending deletes (Delete stages here). Save persists all.
  const [draftFlatPricing, setDraftFlatPricing] = useState([]);
  const [pendingFlatDeletes, setPendingFlatDeletes] = useState([]);
  const lastSavedDefaultPriceRef = useRef(null);
  const draftFlatPricingRef = useRef(draftFlatPricing);
  const flatPricingRef = useRef(flatPricing);
  const pendingFlatDeletesRef = useRef(pendingFlatDeletes);
  draftFlatPricingRef.current = draftFlatPricing;
  flatPricingRef.current = flatPricing;
  pendingFlatDeletesRef.current = pendingFlatDeletes;

  const handleOperatingHoursUpdate = async (silent = false) => {
    try {
      const spaceId = resolveHoursSpaceScope();
      const completeHours = {};
      for (let day = 1; day <= 7; day++) {
        completeHours[day] = operatingHours[day] || {
          is_open: false,
          open_time: '10:00:00',
          close_time: '23:00:00'
        };
      }
      await venueDashboardApiService.updateOperatingHours(venueId, completeHours, spaceId);
      invalidateVenueCaches(venueId);
      if (!silent) notify('success', 'Operating hours updated successfully!');
    } catch (error) {
      if (!silent) notify('error', 'Failed to update operating hours', error.message);
      throw error;
    }
  };

  const handleDefaultPriceUpdate = async () => {
    if (multiSpace) {
      requirePricingSpaceScope();
    }
    if (settingsSpaceId) {
      await venueDashboardApiService.updateVenueSpace(venueId, settingsSpaceId, {
        hourly_price: Number(defaultPrice)
      });
      invalidateVenueCaches(venueId);
      return;
    }
    await venueDashboardApiService.updateDefaultPrice(venueId, defaultPrice);
    const prevPrice = lastSavedDefaultPriceRef.current ?? loadedDefaultPrice;
    if (prevPrice != null && Number(defaultPrice) !== Number(prevPrice)) {
      notify('success', 'Default price updated successfully!', `New price: £${defaultPrice}/hour`);
    }
    lastSavedDefaultPriceRef.current = defaultPrice;
  };

  const handleDefaultPerPersonPriceUpdate = async () => {
    if (multiSpace) {
      requirePricingSpaceScope();
    }
    if (settingsSpaceId) {
      await venueDashboardApiService.updateVenueSpace(venueId, settingsSpaceId, {
        per_person_price: Number(defaultPerPersonPrice)
      });
      invalidateVenueCaches(venueId);
      return;
    }
    await venueDashboardApiService.updateDefaultPerPersonPrice(venueId, defaultPerPersonPrice);
    const prevPrice = loadedDefaultPerPersonPrice;
    if (prevPrice != null && Number(defaultPerPersonPrice) !== Number(prevPrice)) {
      notify('success', 'Default per-person price updated!', `New price: £${defaultPerPersonPrice}/person`);
    }
  };

  const handleHolidayAdd = async () => {
    const validDrafts = draftHolidays.filter(d => d.name?.trim() && d.date);
    if (validDrafts.length === 0) {
      const hasAnyInput = draftHolidays.some(d => d.name?.trim() || d.date);
      if (hasAnyInput) {
        notify('error', 'Please enter both holiday name and date for each row');
      }
      return;
    }

    try {
      for (const d of validDrafts) {
        const holidayData = {
          venue_id: venueId,
          holiday_name: d.name.trim(),
          holiday_date: d.date,
          is_closed: true
        };
        await venueDashboardApiService.createHoliday(venueId, holidayData);
      }
      const updatedHolidays = await venueDashboardApiService.getHolidays(venueId);
      setHolidays(updatedHolidays);
      setDraftHolidays([{ name: '', date: '' }]);
      invalidateVenueCaches(venueId);
      notify('success', `${validDrafts.length} holiday(s) added successfully!`);
    } catch (error) {
      notify('error', 'Failed to add holiday', error.message);
    }
  };

  const onNewHolidayChange = (index, { name, date }) => {
    setDraftHolidays(prev => {
      const next = [...prev];
      next[index] = { name: name ?? prev[index]?.name ?? '', date: date ?? prev[index]?.date ?? '' };
      return next;
    });
  };

  const addAnotherHoliday = () => {
    setDraftHolidays(prev => [...prev, { name: '', date: '' }]);
  };

  const removeDraftHoliday = (index) => {
    setDraftHolidays(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : [{ name: '', date: '' }]);
  };

  const handleHolidayDelete = async (holidayId) => {
    try {
      const holidayToDelete = holidays.find(h => h.id === holidayId);
      if (!window.confirm(`Are you sure you want to delete "${holidayToDelete?.holiday_name}"?`)) {
        return;
      }
      await venueDashboardApiService.deleteHoliday(venueId, holidayId);
      setHolidays(holidays.filter(h => h.id !== holidayId));
    } catch (error) {
      notify('error', 'Failed to delete holiday', error.message);
    }
  };

  const addAnotherDynamicPricing = () => {
    setDraftDynamicPricing(prev => [...prev, { date: '', endDate: '', startTime: '', endTime: '', price: '' }]);
  };

  const onDraftPricingChange = (index, { date, endDate, startTime, endTime, price }) => {
    setDraftDynamicPricing(prev => {
      const next = [...prev];
      next[index] = {
        date: date ?? prev[index]?.date ?? '',
        endDate: endDate ?? prev[index]?.endDate ?? '',
        startTime: startTime ?? prev[index]?.startTime ?? '',
        endTime: endTime ?? prev[index]?.endTime ?? '',
        price: price ?? prev[index]?.price ?? ''
      };
      return next;
    });
  };

  const removeDraftPricing = (index) => {
    setDraftDynamicPricing(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : [{ date: '', endDate: '', startTime: '', endTime: '', price: '' }]);
  };

  const handleDynamicPricingAdd = async () => {
    const validDrafts = draftDynamicPricing.filter(d => d.date && d.price && parseFloat(d.price) > 0);
    if (validDrafts.length === 0) {
      const hasAnyInput = draftDynamicPricing.some(d => d.date || d.endDate || d.startTime || d.endTime || d.price);
      if (hasAnyInput) {
        notify('error', 'Please enter a date and valid price for each row');
      }
      return;
    }

    try {
      const spaceId = multiSpace ? requirePricingSpaceScope() : settingsSpaceId || null;
      for (const d of validDrafts) {
        const endDate = d.endDate && d.endDate >= d.date ? d.endDate : d.date;
        const ruleLabel = d.date === endDate ? d.date : `${d.date} to ${endDate}`;
        const pricingData = {
          venue_id: venueId,
          rule_name: `Dynamic Pricing - ${ruleLabel}`,
          start_date: d.date,
          end_date: endDate,
          start_time: d.startTime || null,
          end_time: d.endTime || null,
          price_per_hour: parseFloat(d.price),
          is_active: true,
          ...(spaceId ? { space_id: spaceId } : {})
        };
        await venueDashboardApiService.createDynamicPricing(venueId, pricingData);
      }
      const updatedPricing = await venueDashboardApiService.getDynamicPricing(venueId, spaceId);
      setDynamicPricing(updatedPricing);
      setDraftDynamicPricing([{ date: '', endDate: '', startTime: '', endTime: '', price: '' }]);
      notify('success', `${validDrafts.length} dynamic pricing rule(s) added successfully!`);
    } catch (error) {
      notify('error', 'Failed to add dynamic pricing', error.message);
    }
  };

  const handleDynamicPricingDelete = async (pricingId) => {
    try {
      await venueDashboardApiService.deleteDynamicPricing(venueId, pricingId);
      setDynamicPricing(dynamicPricing.filter(p => p.id !== pricingId));
    } catch (error) {
      // Silent fail
    }
  };

  const addAnotherDynamicPricingPerPerson = () => {
    setDraftDynamicPricingPerPerson(prev => [...prev, { date: '', endDate: '', startTime: '', endTime: '', price: '' }]);
  };

  const onDraftPricingPerPersonChange = (index, { date, endDate, startTime, endTime, price }) => {
    setDraftDynamicPricingPerPerson(prev => {
      const next = [...prev];
      next[index] = {
        date: date ?? prev[index]?.date ?? '',
        endDate: endDate ?? prev[index]?.endDate ?? '',
        startTime: startTime ?? prev[index]?.startTime ?? '',
        endTime: endTime ?? prev[index]?.endTime ?? '',
        price: price ?? prev[index]?.price ?? ''
      };
      return next;
    });
  };

  const removeDraftPricingPerPerson = (index) => {
    setDraftDynamicPricingPerPerson(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : [{ date: '', endDate: '', startTime: '', endTime: '', price: '' }]);
  };

  const handleDynamicPricingPerPersonAdd = async () => {
    const validDrafts = draftDynamicPricingPerPerson.filter(d => d.date && d.price && parseFloat(d.price) > 0);
    if (validDrafts.length === 0) {
      const hasAnyInput = draftDynamicPricingPerPerson.some(d => d.date || d.endDate || d.startTime || d.endTime || d.price);
      if (hasAnyInput) {
        notify('error', 'Please enter a date and valid price for each per-person rule');
      }
      return;
    }
    try {
      const scopeId = multiSpace
        ? requirePricingSpaceScope()
        : settingsSpaceId || primarySpaceId || null;
      for (const d of validDrafts) {
        const endDate = d.endDate && d.endDate >= d.date ? d.endDate : d.date;
        const pricingData = {
          start_date: d.date,
          end_date: endDate,
          start_time: d.startTime || null,
          end_time: d.endTime || null,
          price_per_person: parseFloat(d.price),
          is_active: true
        };
        if (scopeId) pricingData.space_id = scopeId;
        await venueDashboardApiService.createDynamicPricingPerPerson(venueId, pricingData);
      }
      const updated = await venueDashboardApiService.getDynamicPricingPerPerson(
        venueId,
        settingsSpaceId || null
      );
      setDynamicPricingPerPerson(updated);
      setDraftDynamicPricingPerPerson([{ date: '', endDate: '', startTime: '', endTime: '', price: '' }]);
      notify('success', `${validDrafts.length} per-person pricing rule(s) added!`);
    } catch (error) {
      notify('error', 'Failed to add per-person pricing', error.message);
    }
  };

  const handleDynamicPricingPerPersonDelete = async (pricingId) => {
    try {
      await venueDashboardApiService.deleteDynamicPricingPerPerson(venueId, pricingId);
      setDynamicPricingPerPerson(dynamicPricingPerPerson.filter(p => p.id !== pricingId));
    } catch (error) {
      // Silent fail
    }
  };

  const handleAccountSettingsSave = async () => {
    try {
      const name = String(accountSettings?.name || '').trim();
      const email = String(accountSettings?.email || '').trim();
      const phone = String(accountSettings?.phone || '').trim();

      if (!name || !email || !phone) {
        notify('error', 'Name, email and phone are required');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        notify('error', 'Please enter a valid email address');
        return;
      }

      const { firstName, lastName } = splitAccountName(name);

      await venueDashboardApiService.updateVenueDetails(venueId, {
        contact_name: name,
        contact_email: email,
        contact_phone: phone
      });

      const token = localStorage.getItem('access_token');
      if (token && isOwnerViewingOwnVenue) {
        userProfileService.setAuthToken(token);
        await userProfileService.updateUserProfile({
          firstName,
          lastName,
          phone
        });
      }

      const saved = { name, email, phone };
      setAccountSettings(saved);
      setOwnerAccountSettings?.(saved);
      setVenueDetails((prev) =>
        prev
          ? {
              ...prev,
              contact_name: name,
              contact_email: email,
              contact_phone: phone
            }
          : prev
      );

      invalidateVenueCaches(venueId);
      notify('success', 'Account settings saved successfully!');
    } catch (error) {
      notify('error', 'Failed to save account settings', error.message);
    }
  };

  const addDraftFlatPricing = (rule) => {
    setDraftFlatPricing(prev => [...prev, { ...rule, _draft: true, _draftId: `draft-${Date.now()}-${Math.random().toString(36).slice(2)}` }]);
  };

  const removeDraftFlatPricing = (index) => {
    setDraftFlatPricing(prev => prev.filter((_, i) => i !== index));
  };

  const handleFlatPricingDelete = (pricingId) => {
    if (typeof pricingId === 'string' && pricingId.startsWith('draft-')) {
      setDraftFlatPricing(prev => prev.filter(p => p._draftId !== pricingId));
    } else {
      setPendingFlatDeletes(prev => [...prev, pricingId]);
      setFlatPricing(prev => prev.filter(p => p.id !== pricingId));
    }
  };

  const handleFlatPricingCreateFromDrafts = async () => {
    const valid = draftFlatPricingRef.current.filter(d => d.min_hours > 0 && d.flat_price >= 0);
    if (valid.length === 0) return;
    try {
      const spaceId = multiSpace ? requirePricingSpaceScope() : settingsSpaceId || null;
      for (const d of valid) {
        const payload = {
          min_hours: d.min_hours,
          flat_price: d.flat_price,
          is_active: true,
          ...(spaceId ? { space_id: spaceId } : {})
        };
        await venueDashboardApiService.createFlatPricing(venueId, payload);
      }
      setDraftFlatPricing([]);
      const fresh = await venueDashboardApiService.getFlatPricing(venueId, spaceId);
      setFlatPricing(Array.isArray(fresh) ? fresh : []);
    } catch (error) {
      throw error;
    }
  };

  const handleFlatPricingSync = async () => {
    try {
      const pending = pendingFlatDeletesRef.current;
      const flat = flatPricingRef.current;
      for (const id of pending) {
        await venueDashboardApiService.deleteFlatPricing(venueId, id);
      }
      setPendingFlatDeletes([]);
      for (const row of flat) {
        if (row.id && (row.min_hours != null || row.flat_price != null)) {
          await venueDashboardApiService.updateFlatPricing(venueId, row.id, {
            ...(row.min_hours != null && { min_hours: row.min_hours }),
            ...(row.flat_price != null && { flat_price: row.flat_price })
          });
        }
      }
      const fresh = await venueDashboardApiService.getFlatPricing(venueId, settingsSpaceId);
      setFlatPricing(Array.isArray(fresh) ? fresh : []);
    } catch (error) {
      throw error;
    }
  };

  const handleBookingRulesUpdate = async (rules) => {
    const payload = { ...rules, is_active: rules.is_active !== false };
    if (payload.post_event_buffer_minutes === 0) {
      payload.post_event_buffer_minutes = null;
    }
    await venueDashboardApiService.updateBookingRules(venueId, payload);
    const fresh = await venueDashboardApiService.getBookingRules(venueId);
    setBookingRules(fresh ?? payload);
    invalidateVenueCaches(venueId);
  };

  const handleOperatingHoursPerPersonUpdate = async (silent = false) => {
    try {
      const spaceId = resolveHoursSpaceScope();
      const completeHours = {};
      for (let day = 1; day <= 7; day++) {
        completeHours[day] = operatingHoursPerPerson?.[day] || operatingHours?.[day] || {
          is_open: false,
          open_time: '10:00:00',
          close_time: '23:00:00'
        };
      }
      await venueDashboardApiService.updateOperatingHoursPerPerson(
        venueId,
        completeHours,
        spaceId
      );
      invalidateVenueCaches(venueId);
      if (!silent) notify('success', 'Per-person operating hours updated!');
    } catch (error) {
      if (!silent) notify('error', 'Failed to update per-person operating hours', error.message);
      throw error;
    }
  };

  const handleVenueDetailsUpdate = async (patch = {}) => {
    const details = venueDetails ? { ...venueDetails, ...patch } : null;
    if (!details || !setVenueDetails) {
      return;
    }

    const isHostPhotoOnlySave =
      patch &&
      Object.keys(patch).length === 1 &&
      Object.prototype.hasOwnProperty.call(patch, 'host_photo_url');

    if (isHostPhotoOnlySave) {
      const v = details.host_photo_url;
      const trimmed = typeof v === 'string' ? v.trim() : '';
      const payload = {
        host_photo_url: trimmed
          ? (/^https?:\/\//i.test(trimmed)
              ? trimmed
              : hostPhotoFullUrlFromUserInput(trimmed, venueId))
          : null
      };
      try {
        const updated = await venueDashboardApiService.updateVenueDetails(venueId, payload);
        const normalized = normalizeApiVenueToForm(updated, venueId);
        if (normalized) {
          if (!String(normalized.host_photo_url || '').trim() && trimmed) {
            normalized.host_photo_url = /^https?:\/\//i.test(trimmed)
              ? trimmed
              : hostPhotoFullUrlFromUserInput(trimmed, venueId) || trimmed;
          }
          setVenueDetails(normalized);
        }
        invalidateVenueCaches(venueId);
      } catch (error) {
        notify('error', 'Failed to update host photo', error.message || 'Unknown error');
        throw error;
      }
      return;
    }

    const seating = details.seating_capacity ?? details.max_capacity;
    const inHouseCateringLines = splitMultiline(details.in_house_catering_options_text);
    const externalCateringLines = splitMultiline(details.external_catering_options_text);
    const allowedEventsLines = splitMultiline(details.allowed_events_text);
    const hostRulesLines = syncHostRulesWithAlcoholSettings(
      splitMultiline(details.host_rules_text),
      details
    );
    const amenitiesLines = splitMultiline(details.amenities_text);
    const cancellationPolicy =
      String(details.cancellation_policy_text || '').trim() || undefined;
    const payload = {
      name: details.name || undefined,
      description: details.description || undefined,
      venue_type: details.venue_type || undefined,
      address: details.address || undefined,
      max_capacity: seating != null ? Number(seating) : undefined,
      standing_capacity: details.standing_capacity != null ? Number(details.standing_capacity) : undefined,
      serves_alcohol: details.serves_alcohol,
      outside_alcohol_allowed: details.outside_alcohol_allowed,
      in_house_catering_allowed: details.in_house_catering,
      external_catering_allowed: details.external_catering_allowed,
      cancellation_policy: cancellationPolicy,
      ...(Object.prototype.hasOwnProperty.call(details, 'payment_link')
        ? { payment_link: String(details.payment_link || '').trim() || null }
        : {}),
      facilities: amenitiesLines,
      amenities: {
        food_beverage: {
          inHouseCatering: toAvailableItems(inHouseCateringLines),
          externalCatering: toAvailableItems(externalCateringLines),
          alcohol: {
            venueProvides: Boolean(details.serves_alcohol),
            byoAllowed: Boolean(details.outside_alcohol_allowed),
            corkageFee: Boolean(details.corkage_fee_for_byo)
          }
        },
        event_rules: {
          allowedEvents: toAvailableItems(allowedEventsLines),
          hostRules: hostRulesLines,
          host_rules: hostRulesLines,
          cancellationPolicy: cancellationPolicy || ''
        },
        amenities: toAvailableItems(amenitiesLines)
      }
    };
    if (Object.prototype.hasOwnProperty.call(details, 'host_photo_url')) {
      const v = details.host_photo_url;
      payload.host_photo_url =
        typeof v === 'string' && v.trim()
          ? hostPhotoFullUrlFromUserInput(v.trim(), venueId)
          : null;
    }
    const filtered = Object.fromEntries(
      Object.entries(payload).filter(
        ([, v]) => v !== undefined && v !== '' && v !== null
      )
    );
    const paymentLinkEntry = Object.entries(payload).find(([k]) => k === 'payment_link');
    if (paymentLinkEntry && paymentLinkEntry[1] === null) {
      filtered.payment_link = null;
    }
    const hostPhotoEntry = Object.entries(payload).find(([k]) => k === 'host_photo_url');
    if (hostPhotoEntry && hostPhotoEntry[1] === null) {
      filtered.host_photo_url = null;
    }
    if (Object.keys(filtered).length === 0) {
      notify('error', 'No changes to save.');
      return;
    }
    try {
      const updated = await venueDashboardApiService.updateVenueDetails(venueId, filtered);
      const normalized = normalizeApiVenueToForm(updated, venueId);
      if (normalized) setVenueDetails(normalized);
      if (
        primarySpaceId &&
        seating != null &&
        Number.isFinite(Number(seating)) &&
        Number(seating) > 0
      ) {
        try {
          await venueDashboardApiService.updateVenueSpace(venueId, primarySpaceId, {
            capacity_total: Number(seating)
          });
        } catch {
          // Non-blocking: venue details saved; space capacity can be retried
        }
      }
      invalidateVenueCaches(venueId);
      notify('success', 'Venue details updated successfully!');
    } catch (error) {
      notify('error', 'Failed to update venue details', error.message || 'Unknown error');
    }
  };

  const handleSave = (activeSubSection, patch) => {
    if (activeSubSection === 'venue-details') {
      return handleVenueDetailsUpdate(patch || {});
    } else if (activeSubSection === 'operating-hours') {
      const hasDraftHolidays = draftHolidays.some(d => d.name?.trim() || d.date);
      if (hasDraftHolidays) {
        handleHolidayAdd();
      } else {
        (async () => {
          try {
            await handleOperatingHoursUpdate(true);
            await handleOperatingHoursPerPersonUpdate(true);
            notify('success', 'Operating hours updated successfully!');
            invalidateVenueCaches(venueId);
          } catch (e) {
            notify('error', 'Failed to update operating hours', e.message);
          }
        })();
      }
    } else if (activeSubSection === 'pricing-controls') {
      (async () => {
        const drafts = draftFlatPricingRef.current;
        const flat = flatPricingRef.current;
        const pending = pendingFlatDeletesRef.current;
        const hasDraftPricing = draftDynamicPricing.some(d => d.date || d.price);
        const hasDraftPricingPerPerson = draftDynamicPricingPerPerson.some(d => d.date || d.price);
        const hasDraftFlat = drafts.some(d => d.min_hours > 0 && d.flat_price >= 0);
        const hasFlatChanges = flat.length > 0 || pending.length > 0;
        const hasAnyPricingChanges = hasDraftPricing || hasDraftPricingPerPerson || hasDraftFlat || hasFlatChanges;
        try {
          if (hasDraftPricing) await handleDynamicPricingAdd();
          if (hasDraftPricingPerPerson) await handleDynamicPricingPerPersonAdd();
          if (hasDraftFlat) {
            await handleFlatPricingCreateFromDrafts();
          }
          if (hasFlatChanges) {
            await handleFlatPricingSync();
          }
          await handleDefaultPriceUpdate();
          await handleDefaultPerPersonPriceUpdate();
          if (
            bookingRules &&
            (bookingRules.min_hours_exclusive != null ||
              bookingRules.min_hours_per_person != null ||
              bookingRules.post_event_buffer_minutes !== undefined ||
              bookingRules.per_person_enabled !== undefined)
          ) {
            await handleBookingRulesUpdate(bookingRules);
          }
          notify('success', hasAnyPricingChanges ? 'Pricing controls saved!' : 'Settings saved.');
          invalidateVenueCaches(venueId);
        } catch (e) {
          notify('error', 'Failed to save pricing', e.message || 'Unknown error');
        }
      })();
    }
  };

  return {
    // State
    draftHolidays,
    draftDynamicPricing,
    draftDynamicPricingPerPerson,
    draftFlatPricing,
    addDraftFlatPricing,
    removeDraftFlatPricing,
    // Handlers
    handleFlatPricingDelete,
    handleBookingRulesUpdate,
    handleOperatingHoursPerPersonUpdate,
    handleOperatingHoursUpdate,
    handleDefaultPriceUpdate,
    handleHolidayAdd,
    handleHolidayDelete,
    onNewHolidayChange,
    addAnotherHoliday,
    removeDraftHoliday,
    addAnotherDynamicPricing,
    onDraftPricingChange,
    removeDraftPricing,
    handleDynamicPricingAdd,
    handleDynamicPricingDelete,
    addAnotherDynamicPricingPerPerson,
    onDraftPricingPerPersonChange,
    removeDraftPricingPerPerson,
    handleDynamicPricingPerPersonAdd,
    handleDynamicPricingPerPersonDelete,
    handleAccountSettingsSave,
    handleSave
  };
};

